import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useNotifications } from '../context/NotificationContext';
import '../components/Common/Common.css';

function Contacts() {
  const { markAsViewed, newItems } = useNotifications();
  const [contacts, setContacts] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [flashingItems, setFlashingItems] = useState([]);

  useEffect(() => {
    fetchContacts();
    fetchStatistics();
  }, [statusFilter, searchQuery, currentPage]);

  // Mark all contacts as viewed when page loads
  useEffect(() => {
    markAsViewed('contacts');
  }, []);

  // Flash new items
  useEffect(() => {
    if (newItems.contacts && newItems.contacts.length > 0) {
      const newIds = newItems.contacts.map(item => item.id);
      setFlashingItems(newIds);

      const timer = setTimeout(() => {
        setFlashingItems([]);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [newItems.contacts]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 15,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await api.get('/contacts', { params });
      setContacts(response.data.data);
      setTotalPages(response.data.last_page);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/contacts/statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const handleViewContact = async (contact) => {
    try {
      const response = await api.get(`/contacts/${contact.id}`);
      setSelectedContact(response.data);
      setShowModal(true);
      // Refresh list to update read status
      fetchContacts();
      fetchStatistics();
    } catch (error) {
      console.error('Failed to fetch contact details:', error);
    }
  };

  const handleUpdateStatus = async (contactId, status) => {
    try {
      await api.put(`/contacts/${contactId}`, { status });
      setShowModal(false);
      setSelectedContact(null);
      fetchContacts();
      fetchStatistics();
    } catch (error) {
      console.error('Failed to update contact:', error);
    }
  };

  const handleUpdateNotes = async (contactId, admin_notes) => {
    try {
      await api.put(`/contacts/${contactId}`, { admin_notes });
      alert('Notes saved successfully');
      fetchContacts();
    } catch (error) {
      console.error('Failed to update notes:', error);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      await api.delete(`/contacts/${contactId}`);
      setShowModal(false);
      setSelectedContact(null);
      fetchContacts();
      fetchStatistics();
    } catch (error) {
      console.error('Failed to delete contact:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { color: '#3b82f6', bg: '#dbeafe', text: 'New' },
      read: { color: '#6b7280', bg: '#f3f4f6', text: 'Read' },
      replied: { color: '#10b981', bg: '#d1fae5', text: 'Replied' },
      archived: { color: '#9ca3af', bg: '#f9fafb', text: 'Archived' },
    };

    const badge = badges[status] || badges.new;

    return (
      <span
        style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          color: badge.color,
          backgroundColor: badge.bg,
        }}
      >
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading && contacts.length === 0) {
    return <div className="loading">Loading contacts...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>Contact Submissions</h1>
      </div>

      {/* Statistics */}
      {statistics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div className="stat-card" style={{ background: '#3b82f6', color: 'white' }}>
            <h3>{statistics.total}</h3>
            <p>Total Contacts</p>
          </div>
          <div className="stat-card" style={{ background: '#10b981', color: 'white' }}>
            <h3>{statistics.new}</h3>
            <p>New</p>
          </div>
          <div className="stat-card" style={{ background: '#f59e0b', color: 'white' }}>
            <h3>{statistics.today}</h3>
            <p>Today</p>
          </div>
          <div className="stat-card" style={{ background: '#8b5cf6', color: 'white' }}>
            <h3>{statistics.this_week}</h3>
            <p>This Week</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: '1', minWidth: '250px' }}>
              <input
                type="text"
                placeholder="Search by name, email, subject, or message..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="replied">Replied</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Subject</th>
                <th>Received</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    No contacts found
                  </td>
                </tr>
              ) : (
                contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className={flashingItems.includes(contact.id) ? 'flash-row' : ''}
                    style={{ fontWeight: contact.status === 'new' ? 'bold' : 'normal' }}
                  >
                    <td>{getStatusBadge(contact.status)}</td>
                    <td>{contact.name}</td>
                    <td>{contact.email}</td>
                    <td>{contact.phone || '—'}</td>
                    <td>{contact.subject || 'No subject'}</td>
                    <td>{formatDate(contact.created_at)}</td>
                    <td>
                      <button
                        onClick={() => handleViewContact(contact)}
                        className="btn btn-sm btn-primary"
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer" style={{ display: 'flex', justifyContent: 'center', gap: '10px', padding: '15px' }}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <span style={{ padding: '8px 16px' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Contact Detail Modal */}
      {showModal && selectedContact && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="card"
            style={{ maxWidth: '700px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Contact Details</h2>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            <div className="card-body">
              <div style={{ marginBottom: '20px' }}>
                <strong>Status:</strong> {getStatusBadge(selectedContact.status)}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Name:</strong> {selectedContact.name}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Email:</strong>{' '}
                <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a>
              </div>
              {selectedContact.phone && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Phone:</strong> {selectedContact.phone}
                </div>
              )}
              {selectedContact.subject && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Subject:</strong> {selectedContact.subject}
                </div>
              )}
              <div style={{ marginBottom: '15px' }}>
                <strong>Received:</strong> {formatDate(selectedContact.created_at)}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Message:</strong>
                <div
                  style={{
                    marginTop: '8px',
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {selectedContact.message}
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <strong>Change Status:</strong>
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                  <button
                    onClick={() => handleUpdateStatus(selectedContact.id, 'read')}
                    className="btn btn-sm btn-secondary"
                  >
                    Mark as Read
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(selectedContact.id, 'replied')}
                    className="btn btn-sm btn-success"
                  >
                    Mark as Replied
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(selectedContact.id, 'archived')}
                    className="btn btn-sm"
                    style={{ background: '#6b7280', color: 'white' }}
                  >
                    Archive
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <strong>Admin Notes:</strong>
                <textarea
                  value={selectedContact.admin_notes || ''}
                  onChange={(e) =>
                    setSelectedContact({ ...selectedContact, admin_notes: e.target.value })
                  }
                  rows="4"
                  style={{ width: '100%', marginTop: '8px', padding: '8px' }}
                  placeholder="Add internal notes about this contact..."
                />
                <button
                  onClick={() => handleUpdateNotes(selectedContact.id, selectedContact.admin_notes)}
                  className="btn btn-primary"
                  style={{ marginTop: '8px' }}
                >
                  Save Notes
                </button>
              </div>
            </div>
            <div className="card-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => handleDelete(selectedContact.id)} className="btn btn-danger">
                Delete Contact
              </button>
              <button onClick={() => setShowModal(false)} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contacts;
